/**
 * signageService.js
 *
 * Ground-level navigation signages for the Matterport tour.
 * Renders arrows and/or text as 3D planes laid flat on the ground using
 * CanvasTexture for multilingual text (including Devanagari).
 *
 * Config: public/config/signageConfig.json
 *
 * Usage:
 *   signageService.initialize(sdk, language)   — call once after SDK connects
 *   signageService.setLanguage(lang)           — update labels when language changes
 *   signageService.destroy()                   — call on component unmount
 */

const COMPONENT_NAME = 'mp.groundSignage';

// Module-level registries
const _meshRegistry = new Map();    // id → THREE.Mesh
const _canvasRegistry = new Map();  // id → { canvas, ctx }

// Capture THREE ref from component context
let _THREE = null;

// Counter for unique IDs — seeded from existing signages on load
let _idCounter = 0;

/**
 * Generate a unique signage ID. Uses timestamp + counter to avoid collisions.
 */
function generateUniqueId(existingIds) {
  _idCounter++;
  let id = `sign_${String(_idCounter).padStart(3, '0')}`;
  // If collision, keep incrementing
  while (existingIds && existingIds.has(id)) {
    _idCounter++;
    id = `sign_${String(_idCounter).padStart(3, '0')}`;
  }
  return id;
}

/**
 * Ensure all signages in the array have unique IDs.
 * Fixes duplicates by assigning new IDs.
 */
function deduplicateIds(signages) {
  const seen = new Set();
  for (const s of signages) {
    if (!s.id || seen.has(s.id)) {
      const oldId = s.id;
      s.id = generateUniqueId(seen);
      console.warn(`🪧 Duplicate/missing id "${oldId}" → reassigned to "${s.id}"`);
    }
    seen.add(s.id);
  }
  // Update counter to be at least as high as the highest numeric ID
  for (const s of signages) {
    const match = s.id.match(/^sign_(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num >= _idCounter) _idCounter = num;
    }
  }
  return signages;
}

// ─── Canvas Drawing Helpers ───────────────────────────────────────────────────

function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/**
 * Draw an arrow chevron pointing up (forward direction).
 */
function drawArrow(ctx, cx, cy, size, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  // Chevron / arrow pointing up
  const hw = size * 0.5;
  const hh = size * 0.4;
  ctx.moveTo(cx, cy - hh);           // tip
  ctx.lineTo(cx + hw, cy + hh);      // bottom right
  ctx.lineTo(cx + hw * 0.3, cy + hh * 0.2); // inner right
  ctx.lineTo(cx, cy - hh * 0.3);     // inner tip
  ctx.lineTo(cx - hw * 0.3, cy + hh * 0.2); // inner left
  ctx.lineTo(cx - hw, cy + hh);      // bottom left
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Draw text with a background rectangle. Supports multiline via \n.
 */
function drawTextWithBg(ctx, text, cx, cy, fontSize, textColor, bgColor, bgOpacity) {
  ctx.save();
  ctx.font = `bold ${fontSize}px "Noto Sans Devanagari", "Noto Sans", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Split on literal \n for multiline support
  const lines = text.split('\n');
  const lineHeight = fontSize * 1.35;
  const totalTextHeight = lines.length * lineHeight;

  // Measure widest line
  let maxWidth = 0;
  for (const line of lines) {
    const w = ctx.measureText(line.trim()).width;
    if (w > maxWidth) maxWidth = w;
  }

  const padX = fontSize * 0.5;
  const padY = fontSize * 0.4;

  // Background rect covering all lines
  if (bgOpacity > 0) {
    ctx.fillStyle = bgColor;
    ctx.globalAlpha = bgOpacity;
    const r = fontSize * 0.15;
    const rx = cx - maxWidth / 2 - padX;
    const ry = cy - totalTextHeight / 2 - padY;
    const rw = maxWidth + padX * 2;
    const rh = totalTextHeight + padY * 2;
    ctx.beginPath();
    ctx.roundRect(rx, ry, rw, rh, r);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // Draw each line
  ctx.fillStyle = textColor;
  const startY = cy - (totalTextHeight - lineHeight) / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i].trim(), cx, startY + i * lineHeight);
  }
  ctx.restore();
}

/**
 * Render a signage config to a canvas and return it.
 */
function renderSignageCanvas(config, language) {
  const type = config.type || 'arrow_text';
  const label = config.label?.[language] || config.label?.en || '';
  // Count lines for canvas sizing
  const lines = label ? label.split('\n') : [];
  const lineCount = Math.max(1, lines.length);
  // Taller canvas for multiline text
  const baseTextH = type === 'arrow_text' ? 256 : 256;
  const extraH = Math.max(0, (lineCount - 1) * 80);
  const canvasW = 512;
  const canvasH = (type === 'arrow_text' ? 256 + baseTextH : baseTextH) + extraH;
  const canvas = createCanvas(canvasW, canvasH);
  const ctx = canvas.getContext('2d');

  // Clear with transparency
  ctx.clearRect(0, 0, canvasW, canvasH);

  const arrowColor = config.arrowColor || '#FFD700';
  const textColor = config.textColor || '#FFFFFF';
  const bgColor = config.bgColor || '#000000';
  const bgOpacity = config.bgOpacity ?? 0.7;

  if (type === 'arrow' || type === 'arrow_text') {
    const arrowY = type === 'arrow_text' ? 256 * 0.45 : canvasH * 0.5;
    drawArrow(ctx, canvasW / 2, arrowY, canvasW * 0.5, arrowColor);
  }

  if (type === 'text' || type === 'arrow_text') {
    if (label) {
      // Font size based on longest line
      const longestLine = lines.reduce((a, b) => a.length > b.length ? a : b, '');
      const fontSize = Math.min(56, canvasW / (longestLine.length * 0.8 + 2));
      const textY = type === 'arrow_text'
        ? 256 + (canvasH - 256) / 2
        : canvasH / 2;
      drawTextWithBg(ctx, label, canvasW / 2, textY, fontSize, textColor, bgColor, bgOpacity);
    }
  }

  return canvas;
}

// ─── Ground Signage Component ─────────────────────────────────────────────────

function GroundSignageComponent() {
  this.inputs = {
    signageId: '',
    worldX: 0,
    worldY: 0.05,
    worldZ: 0,
    rotationY: 0,
    scale: 1.0,
    canvasW: 512,
    canvasH: 512,
  };
  this._mesh = null;
  this._mat = null;
  this._texture = null;
}

GroundSignageComponent.prototype.onInit = function () {
  try {
    const THREE = this.context.three;
    _THREE = THREE; // capture for later use

    const { signageId, worldX, worldY, worldZ, rotationY, scale, canvasW, canvasH } = this.inputs;
    const canvasData = _canvasRegistry.get(signageId);

    if (!canvasData) {
      console.warn(`🪧 GroundSignageComponent: no canvas registered for "${signageId}"`);
      return;
    }

    this._texture = new THREE.CanvasTexture(canvasData.canvas);
    this._texture.needsUpdate = true;

    const aspect = canvasW / canvasH;
    const planeW = scale * aspect;
    const planeH = scale;

    const geo = new THREE.PlaneGeometry(planeW, planeH);
    this._mat = new THREE.MeshBasicMaterial({
      map: this._texture,
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
    });

    this._mesh = new THREE.Mesh(geo, this._mat);
    this._mesh.position.set(worldX, worldY, worldZ);
    this._mesh.rotation.x = -Math.PI / 2; // lay flat on ground
    this._mesh.rotation.z = (rotationY * Math.PI) / 180; // direction

    this.context.scene.add(this._mesh);
    _meshRegistry.set(signageId, this._mesh);

    console.log(`🪧 Signage "${signageId}" placed at (${worldX.toFixed(2)}, ${worldY.toFixed(2)}, ${worldZ.toFixed(2)}) rot=${rotationY}°`);
  } catch (err) {
    console.error('🪧 GroundSignageComponent.onInit failed:', err?.message, err);
  }
};

GroundSignageComponent.prototype.onDestroy = function () {
  const id = this.inputs.signageId;
  if (this._mesh) {
    if (this.context?.scene) this.context.scene.remove(this._mesh);
    this._mesh.geometry?.dispose();
  }
  if (this._mat) {
    this._mat.map?.dispose();
    this._mat.dispose();
  }
  this._texture = null;
  _meshRegistry.delete(id);
};

// ─── Service ──────────────────────────────────────────────────────────────────

class SignageService {
  constructor() {
    this._sdk = null;
    this._signages = [];        // config array from JSON
    this._instances = new Map(); // id → { sceneObject }
    this._registered = false;
    this._language = 'mr';
    this._configUrl = '/config/signageConfig.json';

    window._signageService = this; // console debugging
  }

  // ── Initialize ──────────────────────────────────────────────────────────────

  async initialize(sdk, language = 'mr') {
    this._sdk = sdk;
    this._language = language;

    if (!this._registered) {
      try {
        await sdk.Scene.register(COMPONENT_NAME, function () {
          return new GroundSignageComponent();
        });
        this._registered = true;
        console.log('🪧 SignageService: component registered');
      } catch (err) {
        console.warn('🪧 SignageService: failed to register component:', err?.message);
        return;
      }
    }

    await this.loadConfig();
  }

  // ── Config Loading ──────────────────────────────────────────────────────────

  async loadConfig() {
    try {
      const resp = await fetch(this._configUrl + '?t=' + Date.now());
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const config = await resp.json();
      this._signages = deduplicateIds(config.signages || []);
      console.log(`🪧 SignageService: loaded ${this._signages.length} signage(s)`);
    } catch (err) {
      console.warn('🪧 SignageService: failed to load config:', err?.message);
      this._signages = [];
      return;
    }

    // Create all signages
    for (const s of this._signages) {
      await this._createSignage(s).catch(err =>
        console.warn(`🪧 SignageService: signage "${s.id}" failed:`, err?.message)
      );
    }
  }

  // ── Create Single Signage ──────────────────────────────────────────────────

  async _createSignage(config) {
    if (!this._sdk) return;

    const canvas = renderSignageCanvas(config, this._language);
    _canvasRegistry.set(config.id, { canvas, ctx: canvas.getContext('2d') });

    const [sceneObject] = await this._sdk.Scene.createObjects(1);
    const node = sceneObject.addNode();

    node.addComponent(COMPONENT_NAME, {
      signageId: config.id,
      worldX: config.position?.x ?? 0,
      worldY: config.position?.y ?? 0.05,
      worldZ: config.position?.z ?? 0,
      rotationY: config.rotation?.y ?? 0,
      scale: config.scale ?? 1.0,
      canvasW: canvas.width,
      canvasH: canvas.height,
    });

    sceneObject.start();
    this._instances.set(config.id, { sceneObject });
    console.log(`🪧 Signage "${config.id}" created`);
  }

  // ── Add Signage (from editor) ──────────────────────────────────────────────

  async addSignage(config) {
    // Ensure unique ID
    const existingIds = new Set(this._signages.map(s => s.id));
    if (!config.id || existingIds.has(config.id)) {
      config.id = generateUniqueId(existingIds);
    }
    this._signages.push(config);
    await this._createSignage(config);
    return config.id; // return the (possibly reassigned) id
  }

  // ── Update Signage ─────────────────────────────────────────────────────────

  updateSignage(id, updates) {
    const config = this._signages.find(s => s.id === id);
    if (!config) return;

    // Merge nested objects carefully — do NOT let Object.assign overwrite them
    if (updates.position) config.position = { ...config.position, ...updates.position };
    if (updates.rotation) config.rotation = { ...config.rotation, ...updates.rotation };
    if (updates.label) config.label = { ...config.label, ...updates.label };

    // Merge flat keys (skip nested ones already handled above)
    const { position: _p, rotation: _r, label: _l, ...flatUpdates } = updates;
    Object.assign(config, flatUpdates);

    // Update 3D mesh transform
    const mesh = _meshRegistry.get(id);
    if (mesh) {
      if (updates.position) {
        mesh.position.set(
          config.position.x ?? mesh.position.x,
          config.position.y ?? mesh.position.y,
          config.position.z ?? mesh.position.z
        );
      }
      if (updates.rotation) {
        mesh.rotation.z = ((config.rotation?.y ?? 0) * Math.PI) / 180;
      }
      if (updates.scale !== undefined) {
        const canvasData = _canvasRegistry.get(id);
        if (canvasData) {
          const aspect = canvasData.canvas.width / canvasData.canvas.height;
          mesh.scale.set(config.scale * aspect, config.scale, 1);
        }
      }
    }

    // Re-render canvas texture if visual properties changed
    if (updates.label || updates.type || updates.arrowColor || updates.textColor || updates.bgColor || updates.bgOpacity) {
      this._rebuildTexture(id, config);
    }
  }

  _rebuildTexture(id, config) {
    const canvas = renderSignageCanvas(config, this._language);
    _canvasRegistry.set(id, { canvas, ctx: canvas.getContext('2d') });

    const mesh = _meshRegistry.get(id);
    if (mesh && mesh.material && _THREE) {
      if (mesh.material.map) mesh.material.map.dispose();
      mesh.material.map = new _THREE.CanvasTexture(canvas);
      mesh.material.needsUpdate = true;

      // Rebuild geometry if canvas aspect ratio changed (multiline text)
      const scale = config.scale ?? 1.0;
      const aspect = canvas.width / canvas.height;
      const oldGeo = mesh.geometry;
      mesh.geometry = new _THREE.PlaneGeometry(scale * aspect, scale);
      oldGeo?.dispose();
    }
  }

  // ── Remove Signage ─────────────────────────────────────────────────────────

  removeSignage(id) {
    const inst = this._instances.get(id);
    if (inst) {
      try { inst.sceneObject.stop(); } catch (_) {}
      this._instances.delete(id);
    }
    _meshRegistry.delete(id);
    _canvasRegistry.delete(id);
    this._signages = this._signages.filter(s => s.id !== id);
    console.log(`🪧 Signage "${id}" removed`);
  }

  // ── Language Update ────────────────────────────────────────────────────────

  setLanguage(language) {
    if (this._language === language) return;
    this._language = language;
    // Rebuild all textures with new language
    for (const config of this._signages) {
      this._rebuildTexture(config.id, config);
    }
  }

  // ── Export Config ──────────────────────────────────────────────────────────

  exportConfig() {
    return JSON.stringify({ signages: this._signages }, null, 2);
  }

  getSignages() {
    return this._signages;
  }

  getSDK() {
    return this._sdk;
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  destroy() {
    for (const [, inst] of this._instances) {
      try { inst.sceneObject.stop(); } catch (_) {}
    }
    this._instances.clear();
    _meshRegistry.clear();
    _canvasRegistry.clear();
    this._signages = [];
    console.log('🪧 SignageService: destroyed');
  }
}

const signageService = new SignageService();
export default signageService;
